import React, { useState, useEffect } from 'react';
import { SiteHeader, SiteFooter } from '@/components/SiteLayout';
import {
  ChevronRight, BookOpen, Zap, AlertTriangle, CheckCircle,
  Copy, Check, ChevronDown, Link2, Plug, Code, Send,
  Webhook, FileJson, LayoutList, ArrowRight, HelpCircle,
  Shield, Share2, MessageSquare, Mail, ShoppingCart,
  Smartphone, Globe, Users, Server
} from 'lucide-react';

const SIDEBAR = [
  { id: 'overview', label: 'Overview', icon: BookOpen },
  { id: 'integration-modes', label: 'Integration Modes', icon: Plug },
  { id: 'payment-links', label: 'Payment Links', icon: Link2 },
  { id: 'auto-webhooks', label: 'Auto-Managed Webhooks', icon: Webhook },
  { id: 'postman-collection', label: 'Postman Collection', icon: FileJson },
  { id: 'api-reference', label: 'API Reference', icon: Code },
  { id: 'webhook-events', label: 'Webhook Events', icon: Zap },
  { id: 'choosing-integration', label: 'Choosing the Right Integration', icon: HelpCircle },
  { id: 'best-practices', label: 'Best Practices', icon: Shield },
];

function CodeBlock({ code, title }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="rounded-xl border border-border overflow-hidden my-4">
      {title && (
        <div className="flex items-center justify-between px-4 py-2 bg-muted/50 border-b border-border">
          <span className="text-xs font-mono text-muted-foreground">{title}</span>
          <button onClick={handleCopy} className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground">
            {copied ? <><Check className="h-3 w-3 text-emerald-400" /> Copied</> : <><Copy className="h-3 w-3" /> Copy</>}
          </button>
        </div>
      )}
      <pre className="p-4 overflow-x-auto text-sm bg-[#0d1117] text-gray-300" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
        <code>{code}</code>
      </pre>
    </div>
  );
}

function InfoBox({ type = 'info', title, children }) {
  const styles = {
    info: 'border-primary/30 bg-primary/5',
    warning: 'border-amber-500/30 bg-amber-500/5',
    success: 'border-emerald-500/30 bg-emerald-500/5',
    danger: 'border-red-500/30 bg-red-500/5',
  };
  const icons = { info: Zap, warning: AlertTriangle, success: CheckCircle, danger: Shield };
  const Icon = icons[type];
  const textColor = { info: 'text-primary', warning: 'text-amber-400', success: 'text-emerald-400', danger: 'text-red-400' };
  return (
    <div className={`rounded-xl border p-4 my-4 ${styles[type]}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`h-4 w-4 ${textColor[type]}`} />
        <span className={`text-sm font-semibold ${textColor[type]}`}>{title}</span>
      </div>
      <div className="text-sm text-muted-foreground leading-relaxed">{children}</div>
    </div>
  );
}

function Section({ id, title, children }) {
  return (
    <section id={id} className="scroll-mt-24 pb-12 mb-12 border-b border-border last:border-0">
      <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-6 text-foreground" style={{ fontFamily: "'Outfit', sans-serif" }}>{title}</h2>
      <div className="prose-content space-y-4 text-[15px] text-muted-foreground leading-relaxed">{children}</div>
    </section>
  );
}

function StepFlow({ steps }) {
  return (
    <div className="my-6 space-y-0">
      {steps.map((step, i) => (
        <div key={i} className="flex gap-4">
          <div className="flex flex-col items-center">
            <div className="w-8 h-8 rounded-full bg-primary/10 border border-primary/30 flex items-center justify-center text-xs font-bold text-primary flex-shrink-0">{i + 1}</div>
            {i < steps.length - 1 && <div className="w-px h-full bg-border min-h-[24px]" />}
          </div>
          <div className="pb-6">
            <p className="text-sm font-semibold text-foreground">{step.title}</p>
            <p className="text-sm text-muted-foreground mt-0.5">{step.desc}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

const DocsIntegrationsPage = () => {
  const [activeSection, setActiveSection] = useState('overview');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    document.title = 'Integrations | CoinPayUp Docs';
    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
          break;
        }
      }
    }, { rootMargin: '-100px 0px -60% 0px', threshold: 0 });
    SIDEBAR.forEach(s => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setSidebarOpen(false);
  };

  return (
    <div className="min-h-screen bg-background" data-testid="docs-integrations-page">
      <SiteHeader />

      {/* Page Header */}
      <div className="border-b border-border bg-muted/30">
        <div className="max-w-7xl mx-auto px-6 py-10">
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
            <span>Docs</span><ChevronRight className="h-3.5 w-3.5" /><span>Guides</span><ChevronRight className="h-3.5 w-3.5" /><span className="text-foreground">Integrations</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Integrations
          </h1>
          <p className="text-base text-muted-foreground mt-2 max-w-2xl">
            Connect CoinPayUp to your business — from simple payment links to full API integrations. Start simple, upgrade anytime.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 flex gap-8 relative">
        {/* Mobile sidebar toggle */}
        <button className="lg:hidden fixed bottom-6 right-6 z-40 bg-primary text-white px-4 py-2.5 rounded-full shadow-lg flex items-center gap-2 text-sm font-medium"
          onClick={() => setSidebarOpen(!sidebarOpen)}>
          <BookOpen className="h-4 w-4" /> Sections <ChevronDown className={`h-3.5 w-3.5 transition-transform ${sidebarOpen ? 'rotate-180' : ''}`} />
        </button>

        {/* Sidebar */}
        <aside className={`w-64 flex-shrink-0 lg:block ${sidebarOpen ? 'fixed inset-0 z-30 bg-background/95 backdrop-blur-xl lg:relative lg:bg-transparent p-6 pt-20 lg:p-0 overflow-y-auto' : 'hidden'}`}>
          <nav className="lg:sticky lg:top-24 py-8 space-y-1" data-testid="docs-integrations-sidebar">
            {SIDEBAR.map(item => (
              <button key={item.id} onClick={() => scrollTo(item.id)} data-testid={`sidebar-${item.id}`}
                className={`w-full text-left flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${activeSection === item.id ? 'bg-primary/10 text-primary font-semibold' : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'}`}>
                <item.icon className="h-3.5 w-3.5 flex-shrink-0" />
                <span className="truncate">{item.label}</span>
              </button>
            ))}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 min-w-0 py-8 lg:py-12 max-w-3xl" data-testid="docs-integrations-content">

          {/* OVERVIEW */}
          <Section id="overview" title="Overview">
            <p>
              CoinPayUp makes it easy for any business to <strong className="text-foreground">accept cryptocurrency payments</strong> — whether you're a freelancer sharing invoices on WhatsApp, an online store with a checkout page, or a SaaS platform processing thousands of transactions automatically.
            </p>
            <p>
              You don't need to be a developer to get started. CoinPayUp offers multiple integration methods, from <strong className="text-foreground">zero-code payment links</strong> to <strong className="text-foreground">full API access</strong>. Start with whatever works for your business today, and upgrade to a more advanced setup whenever you're ready.
            </p>
            <p>This guide covers:</p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>Integration modes: no-code, plugins, and API</li>
              <li>Payment links: share a URL, get paid</li>
              <li>Auto-managed webhooks: automatic payment notifications</li>
              <li>Postman collection: test the API without writing code</li>
              <li>API reference: build custom integrations</li>
              <li>Webhook events: real-time payment updates</li>
              <li>How to choose the right integration for your business</li>
              <li>Best practices for a smooth setup</li>
            </ul>
          </Section>

          {/* INTEGRATION MODES */}
          <Section id="integration-modes" title="Integration Modes">
            <p>
              CoinPayUp offers <strong className="text-foreground">three ways</strong> to start accepting crypto payments. Each is designed for a different level of technical experience — pick the one that fits your business.
            </p>

            {/* No-Code */}
            <div className="rounded-xl border border-border p-5 my-6 bg-emerald-500/5 border-emerald-500/20">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                  <Link2 className="h-5 w-5 text-emerald-400" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-foreground">No-Code Integration</h3>
                  <span className="text-xs text-emerald-400 font-medium">Perfect for beginners</span>
                </div>
              </div>
              <p className="text-sm text-muted-foreground mb-3">Accept crypto payments without writing a single line of code or installing anything. Just create a payment link in your CoinPayUp dashboard and share it with your customers.</p>
              <div className="space-y-2 text-sm text-muted-foreground">
                <div className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-emerald-400 mt-0.5 flex-shrink-0" /><span>Create payment links from the dashboard</span></div>
                <div className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-emerald-400 mt-0.5 flex-shrink-0" /><span>Share via email, WhatsApp, social media, or text message</span></div>
                <div className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-emerald-400 mt-0.5 flex-shrink-0" /><span>Get email notifications when payments arrive</span></div>
                <div className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-emerald-400 mt-0.5 flex-shrink-0" /><span>No website, developer, or technical skills required</span></div>
              </div>
              <div className="mt-4 px-3 py-2 rounded-lg bg-muted/50 text-xs text-muted-foreground">
                <strong className="text-foreground">Best for:</strong> Freelancers, consultants, social media sellers, small businesses, and anyone who wants to accept crypto with zero setup.
              </div>
            </div>

            {/* Plugin */}
            <div className="rounded-xl border border-border p-5 my-6 bg-primary/5 border-primary/20">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Plug className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-foreground">Plugin Integration</h3>
                  <span className="text-xs text-primary font-medium">For popular platforms</span>
                </div>
              </div>
              <p className="text-sm text-muted-foreground mb-3">If you're running a website on a popular platform, you can add CoinPayUp as a payment method using a ready-made plugin. No custom code needed — just install, configure your API key, and start accepting payments.</p>
              <div className="space-y-2 text-sm text-muted-foreground">
                <div className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" /><span><strong className="text-foreground">WordPress / WooCommerce</strong> — add crypto payments to your online store</span></div>
                <div className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" /><span><strong className="text-foreground">WHMCS</strong> — accept crypto for hosting and web services</span></div>
                <div className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" /><span>Automatic payment verification and order fulfillment</span></div>
                <div className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" /><span>Pre-built, tested, and maintained by CoinPayUp</span></div>
              </div>
              <div className="mt-4 px-3 py-2 rounded-lg bg-muted/50 text-xs text-muted-foreground">
                <strong className="text-foreground">Best for:</strong> Online stores, web hosting companies, and any business using WordPress, WooCommerce, or WHMCS.
              </div>
            </div>

            {/* API */}
            <div className="rounded-xl border border-border p-5 my-6 bg-amber-500/5 border-amber-500/20">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                  <Code className="h-5 w-5 text-amber-400" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-foreground">API Integration</h3>
                  <span className="text-xs text-amber-400 font-medium">Full control for developers</span>
                </div>
              </div>
              <p className="text-sm text-muted-foreground mb-3">For businesses that need full control over the payment experience. The CoinPayUp API lets you create invoices, track payments, and receive notifications — all programmatically from your own code.</p>
              <div className="space-y-2 text-sm text-muted-foreground">
                <div className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-amber-400 mt-0.5 flex-shrink-0" /><span>Create and manage invoices via REST API</span></div>
                <div className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-amber-400 mt-0.5 flex-shrink-0" /><span>Receive real-time webhook notifications</span></div>
                <div className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-amber-400 mt-0.5 flex-shrink-0" /><span>Build custom checkout flows and payment pages</span></div>
                <div className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-amber-400 mt-0.5 flex-shrink-0" /><span>Automate billing, subscriptions, and transaction management</span></div>
              </div>
              <div className="mt-4 px-3 py-2 rounded-lg bg-muted/50 text-xs text-muted-foreground">
                <strong className="text-foreground">Best for:</strong> SaaS platforms, custom websites, marketplaces, and any business with a developer on their team.
              </div>
            </div>

            <InfoBox type="info" title="You can always upgrade">
              Start with payment links today and upgrade to a plugin or API integration later. Your account, wallets, and payment history carry over — nothing is lost when you switch.
            </InfoBox>
          </Section>

          {/* PAYMENT LINKS */}
          <Section id="payment-links" title="Payment Links">
            <p>
              A payment link is a <strong className="text-foreground">shareable URL</strong> that lets your customer pay you in crypto — without needing a website, app, or any technical integration. You create the link, send it to your customer, and they pay. It's that simple.
            </p>

            <h3 className="text-lg font-semibold text-foreground mt-8 mb-3">How Payment Links Work</h3>
            <StepFlow steps={[
              { title: 'Create a payment link', desc: 'From your CoinPayUp dashboard, click "Create Payment Link." Enter the amount, currency, and an optional description (e.g., "Logo Design — $150 USDT").' },
              { title: 'Share the link', desc: 'Copy the link and send it to your customer via WhatsApp, email, Telegram, Instagram DM, SMS, or any messaging platform.' },
              { title: 'Customer pays', desc: 'Your customer opens the link, sees the payment details, and sends the crypto from their wallet. No account required on their end.' },
              { title: 'You get notified', desc: 'CoinPayUp detects the payment on the blockchain and sends you a confirmation. The payment appears in your dashboard instantly.' },
            ]} />

            <h3 className="text-lg font-semibold text-foreground mt-8 mb-3">Example Payment Link</h3>
            <CodeBlock title="Shareable Payment URL" code={`https://coinpayup.com/p/logo-design-150

This link opens a checkout page showing:
  Amount: 150 USDT
  Description: "Logo Design — Final Delivery"
  Supported tokens: USDT, USDC, BNB
  Wallet address: auto-assigned from your merchant account`} />

            <h3 className="text-lg font-semibold text-foreground mt-8 mb-3">Who Uses Payment Links?</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 my-4">
              {[
                { icon: Users, title: 'Freelancers', desc: 'Send a payment link after completing a project. Get paid in USDT instead of waiting for bank transfers.' },
                { icon: MessageSquare, title: 'Consultants', desc: 'Share a payment link before or after a consulting session. Quick, professional, and borderless.' },
                { icon: Globe, title: 'Remote services', desc: 'Accept payments from clients anywhere in the world — no international fees, no delays.' },
                { icon: Smartphone, title: 'Social media sellers', desc: 'Share your payment link in your Instagram bio, Twitter DMs, or WhatsApp status.' },
              ].map((item, i) => (
                <div key={i} className="p-4 rounded-xl bg-muted/30 border border-border">
                  <div className="flex items-center gap-2 mb-2">
                    <item.icon className="h-4 w-4 text-primary" />
                    <p className="text-sm font-semibold text-foreground">{item.title}</p>
                  </div>
                  <p className="text-xs text-muted-foreground">{item.desc}</p>
                </div>
              ))}
            </div>

            <InfoBox type="success" title="No website needed">
              Payment links work entirely on their own. You don't need a website, an app, or any technical knowledge. If you can send a link via text message, you can accept crypto payments.
            </InfoBox>
          </Section>

          {/* AUTO-MANAGED WEBHOOKS */}
          <Section id="auto-webhooks" title="Auto-Managed Webhooks">
            <p>
              A <strong className="text-foreground">webhook</strong> is an automatic notification that CoinPayUp sends to your system when something happens with a payment. Think of it like a delivery notification — when a package arrives at your door, the courier sends you an alert. Webhooks work the same way: when a payment arrives, CoinPayUp tells your system immediately.
            </p>

            <h3 className="text-lg font-semibold text-foreground mt-8 mb-3">How It Works in Plain English</h3>
            <div className="rounded-xl border border-border p-5 my-4 bg-muted/30">
              <div className="space-y-4 text-sm text-muted-foreground">
                <div className="flex items-start gap-3">
                  <div className="w-7 h-7 rounded-full bg-primary/10 border border-primary/30 flex items-center justify-center text-xs font-bold text-primary flex-shrink-0">1</div>
                  <div><strong className="text-foreground">Customer sends payment</strong> — they transfer crypto to your invoice wallet.</div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-7 h-7 rounded-full bg-primary/10 border border-primary/30 flex items-center justify-center text-xs font-bold text-primary flex-shrink-0">2</div>
                  <div><strong className="text-foreground">CoinPayUp detects it</strong> — our blockchain monitors pick up the transaction automatically.</div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-7 h-7 rounded-full bg-primary/10 border border-primary/30 flex items-center justify-center text-xs font-bold text-primary flex-shrink-0">3</div>
                  <div><strong className="text-foreground">Your system gets notified</strong> — CoinPayUp sends a message to your website's webhook URL with all the payment details.</div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-7 h-7 rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-xs font-bold text-emerald-400 flex-shrink-0">4</div>
                  <div><strong className="text-foreground">Order updates automatically</strong> — your system marks the order as paid, grants access, or triggers the next step — all without you lifting a finger.</div>
                </div>
              </div>
            </div>

            <h3 className="text-lg font-semibold text-foreground mt-8 mb-3">Why Auto-Managed?</h3>
            <p>CoinPayUp handles the hard parts of webhook delivery for you:</p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li><strong className="text-foreground">Automatic retries</strong> — if your server is temporarily down, CoinPayUp will retry the notification up to 6 times with increasing delays</li>
              <li><strong className="text-foreground">Delivery tracking</strong> — every webhook delivery is logged, so you can see what was sent and when</li>
              <li><strong className="text-foreground">Security built-in</strong> — every notification is signed so your server can verify it's really from CoinPayUp</li>
            </ul>

            <h3 className="text-lg font-semibold text-foreground mt-8 mb-3">Benefits at a Glance</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 my-4">
              {[
                { title: 'Automatic updates', desc: 'Your system is always in sync with payment status — no manual checking required.' },
                { title: 'Reliable notifications', desc: 'Built-in retries and delivery tracking ensure you never miss a payment.' },
                { title: 'No missed payments', desc: 'Even if your server goes down briefly, the webhook will be retried until delivered.' },
              ].map((item, i) => (
                <div key={i} className="p-4 rounded-xl bg-muted/30 border border-border text-center">
                  <CheckCircle className="h-5 w-5 text-emerald-400 mx-auto mb-2" />
                  <p className="text-sm font-semibold text-foreground">{item.title}</p>
                  <p className="text-xs text-muted-foreground mt-1">{item.desc}</p>
                </div>
              ))}
            </div>

            <InfoBox type="info" title="Real-world analogy">
              Imagine you order food online. You don't keep refreshing the page to check if it's ready — the restaurant sends you a notification when it's on its way. That's exactly what webhooks do for payments. CoinPayUp "pushes" the update to you, so you don't have to keep asking.
            </InfoBox>
          </Section>

          {/* POSTMAN COLLECTION */}
          <Section id="postman-collection" title="Postman Collection">
            <p>
              <strong className="text-foreground">Postman</strong> is a popular tool that lets developers test API requests without writing any code. Think of it as a form you fill out that sends a request to CoinPayUp and shows you the response — great for trying things out before building your integration.
            </p>

            <h3 className="text-lg font-semibold text-foreground mt-8 mb-3">What Can You Do With It?</h3>
            <p>The CoinPayUp Postman Collection comes pre-configured with all the API endpoints. Here's what you can test:</p>
            <div className="space-y-2 my-4">
              {[
                { title: 'Create a test invoice', desc: 'See exactly what happens when your code creates an invoice — without writing the code first.' },
                { title: 'Check payment status', desc: 'Query an invoice to see its current status, amount received, and payment history.' },
                { title: 'Simulate webhook events', desc: 'Send a test webhook to your endpoint to make sure it handles the payload correctly.' },
                { title: 'Verify API responses', desc: 'See the exact JSON structure returned by each endpoint — useful for planning your integration.' },
              ].map((item, i) => (
                <div key={i} className="flex gap-3 p-3 rounded-lg bg-muted/30 border border-border">
                  <ArrowRight className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-foreground">{item.title}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <h3 className="text-lg font-semibold text-foreground mt-8 mb-3">How to Get Started</h3>
            <StepFlow steps={[
              { title: 'Download Postman', desc: 'Visit postman.com and download the free app (available for Mac, Windows, and Linux).' },
              { title: 'Import the collection', desc: 'In your CoinPayUp dashboard, go to Integrations and click "Download Postman Collection." Import the downloaded JSON file into Postman.' },
              { title: 'Add your API key', desc: 'In Postman, set your CoinPayUp API key in the collection variables. This authenticates your requests.' },
              { title: 'Start testing', desc: 'Open any request in the collection, click "Send," and see the response. It\'s that simple.' },
            ]} />

            <InfoBox type="warning" title="Non-technical? Skip this section">
              If you're using payment links or a plugin integration, you don't need Postman. This section is mainly for developers who are building a custom API integration and want to test things before writing code.
            </InfoBox>
          </Section>

          {/* API REFERENCE */}
          <Section id="api-reference" title="API Reference">
            <p>
              The <strong className="text-foreground">API</strong> (Application Programming Interface) allows your website or app to talk directly to CoinPayUp. Instead of manually creating invoices in the dashboard, your system can do it automatically — creating invoices, checking payment status, and receiving real-time updates, all without human intervention.
            </p>

            <h3 className="text-lg font-semibold text-foreground mt-8 mb-3">What Developers Can Do</h3>
            <div className="overflow-x-auto my-4">
              <table className="w-full text-sm border border-border rounded-lg overflow-hidden">
                <thead>
                  <tr className="bg-muted/50">
                    <th className="text-left px-4 py-2.5 font-semibold text-foreground border-b border-border">Action</th>
                    <th className="text-left px-4 py-2.5 font-semibold text-foreground border-b border-border">What It Does</th>
                    <th className="text-left px-4 py-2.5 font-semibold text-foreground border-b border-border">Example</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Create invoices', 'Generate a payment request with a specific amount and currency', 'Customer clicks "Pay Now" → your app creates a 50 USDT invoice'],
                    ['Check payment status', 'See if an invoice has been paid, partially paid, or is still pending', 'Your app checks every 30 seconds if the invoice is paid'],
                    ['Receive updates', 'Get notified automatically when payment events happen', 'Your server receives a webhook when payment is confirmed'],
                    ['Manage transactions', 'View payment history, filter by date, and export records', 'Your admin panel pulls this month\'s transaction list'],
                  ].map(([action, desc, example], i) => (
                    <tr key={i}>
                      <td className="px-4 py-2.5 border-b border-border text-foreground font-medium">{action}</td>
                      <td className="px-4 py-2.5 border-b border-border text-muted-foreground">{desc}</td>
                      <td className="px-4 py-2.5 border-b border-border text-muted-foreground text-xs">{example}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <h3 className="text-lg font-semibold text-foreground mt-8 mb-3">Quick Example: Creating an Invoice</h3>
            <CodeBlock title="POST /api/invoices" code={`// Your server sends this request to CoinPayUp:
{
  "amount": 50.00,
  "currency": "USDT",
  "network": "bsc",
  "description": "Premium Plan — Monthly Subscription",
  "customer_email": "customer@example.com",
  "external_id": "ORDER-2026-00123",
  "reference": "Customer: John Doe | Email: john@example.com | Plan: Premium"
}

// CoinPayUp responds with:
{
  "invoice_id": "INV-20260221-X7K9P2",
  "status": "pending",
  "amount": 50.00,
  "wallet_address": "0xABC123...",
  "checkout_url": "https://coinpayup.com/checkout/INV-20260221-X7K9P2",
  "expires_at": "2026-02-21T16:30:00Z"
}`} />

            <h3 className="text-lg font-semibold text-foreground mt-8 mb-3">Invoice Fields Reference</h3>
            <div className="rounded-xl border border-border overflow-x-auto mb-6">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted/50">
                    <th className="text-left px-4 py-2.5 font-semibold text-foreground border-b border-border">Field</th>
                    <th className="text-left px-4 py-2.5 font-semibold text-foreground border-b border-border">Type</th>
                    <th className="text-left px-4 py-2.5 font-semibold text-foreground border-b border-border">Required</th>
                    <th className="text-left px-4 py-2.5 font-semibold text-foreground border-b border-border">Description</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['amount', 'number', 'Yes', 'Payment amount in the specified fiat currency'],
                    ['currency', 'string', 'Yes', 'Fiat currency code: USDT, USD, EUR, etc.'],
                    ['network', 'string', 'No', 'Blockchain network: bsc, tron, polygon, etc.'],
                    ['description', 'string', 'No', 'Human-readable payment description shown to the customer'],
                    ['customer_email', 'string', 'No', 'Customer email — used for payment notifications'],
                    ['external_id', 'string', 'No', 'Your internal order/reference ID (e.g. WooCommerce order ID)'],
                    ['reference', 'string', 'No', 'Free-form reference field — store customer name, email, notes, or any additional info you need attached to this payment'],
                    ['success_url', 'string', 'No', 'URL to redirect the customer after successful payment'],
                    ['cancel_url', 'string', 'No', 'URL to redirect the customer after cancellation or expiry'],
                  ].map(([field, type, req, desc], i) => (
                    <tr key={i}>
                      <td className="px-4 py-2.5 border-b border-border font-mono text-primary text-xs">{field}</td>
                      <td className="px-4 py-2.5 border-b border-border text-muted-foreground text-xs">{type}</td>
                      <td className="px-4 py-2.5 border-b border-border text-xs">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${req === 'Yes' ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'}`}>{req}</span>
                      </td>
                      <td className="px-4 py-2.5 border-b border-border text-muted-foreground text-xs">{desc}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <h3 className="text-lg font-semibold text-foreground mt-8 mb-3">Who Needs API Integration?</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 my-4">
              {[
                { icon: Server, title: 'SaaS platforms', desc: 'Automate subscription billing and access management.' },
                { icon: ShoppingCart, title: 'Custom checkout', desc: 'Build your own payment page that matches your brand.' },
                { icon: Users, title: 'Marketplaces', desc: 'Handle payments between buyers and multiple sellers.' },
                { icon: LayoutList, title: 'Automated billing', desc: 'Generate invoices and collect payments on a schedule.' },
              ].map((item, i) => (
                <div key={i} className="p-4 rounded-xl bg-muted/30 border border-border">
                  <div className="flex items-center gap-2 mb-2">
                    <item.icon className="h-4 w-4 text-primary" />
                    <p className="text-sm font-semibold text-foreground">{item.title}</p>
                  </div>
                  <p className="text-xs text-muted-foreground">{item.desc}</p>
                </div>
              ))}
            </div>

            <InfoBox type="info" title="Not a developer?">
              If you're not planning to write code, you don't need to use the API directly. Payment links and plugin integrations handle everything for you. The API is for businesses that want to build custom, automated payment flows.
            </InfoBox>
          </Section>

          {/* WEBHOOK EVENTS */}
          <Section id="webhook-events" title="Webhook Events">
            <p>
              Webhook events are <strong className="text-foreground">automatic messages</strong> that CoinPayUp sends to your system when something important happens with a payment. Each event tells your system exactly what happened, so it can take the right action — like marking an order as paid, sending a receipt, or granting access to a product.
            </p>

            <h3 className="text-lg font-semibold text-foreground mt-8 mb-3">Event Types Explained</h3>
            <div className="space-y-3 my-4">
              {[
                { event: 'payment.confirmed', label: 'Payment received', desc: 'A customer\'s payment has been confirmed on the blockchain. This means the crypto has been sent and verified — it\'s real and irreversible.', action: 'Update order status, log the payment, send a receipt.' },
                { event: 'invoice.paid', label: 'Invoice fully paid', desc: 'The invoice has received the full amount (or within tolerance). The order is complete.', action: 'Fulfill the order, activate the subscription, grant access.' },
                { event: 'invoice.underpaid', label: 'Partial payment received', desc: 'The customer sent some money, but not enough to cover the full invoice amount.', action: 'Notify the customer of the remaining balance.' },
                { event: 'invoice.expired', label: 'Invoice expired', desc: 'The invoice reached its expiry time without receiving full payment.', action: 'Cancel the pending order, release reserved inventory.' },
                { event: 'invoice.overpaid', label: 'Customer overpaid', desc: 'The customer sent more than the invoice amount (by more than 1%).', action: 'Flag for review — consider refunding the excess or crediting the customer.' },
              ].map((item, i) => (
                <div key={i} className="rounded-xl border border-border overflow-hidden">
                  <div className="px-4 py-3 bg-muted/50 border-b border-border flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <code className="px-1.5 py-0.5 rounded bg-muted text-xs font-mono text-primary">{item.event}</code>
                      <span className="text-sm font-medium text-foreground">{item.label}</span>
                    </div>
                  </div>
                  <div className="px-4 py-3 space-y-1.5">
                    <p className="text-sm text-muted-foreground">{item.desc}</p>
                    <p className="text-sm"><span className="font-medium text-foreground">What to do: </span><span className="text-muted-foreground">{item.action}</span></p>
                  </div>
                </div>
              ))}
            </div>

            <h3 className="text-lg font-semibold text-foreground mt-8 mb-3">Why Events Matter</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 my-4">
              {[
                { title: 'Update order status', desc: 'Automatically mark orders as paid without manually checking the blockchain.' },
                { title: 'Grant access instantly', desc: 'Unlock digital products, activate subscriptions, or open downloads the moment payment confirms.' },
                { title: 'Send confirmations', desc: 'Trigger receipt emails or in-app notifications to your customers immediately.' },
              ].map((item, i) => (
                <div key={i} className="p-4 rounded-xl bg-muted/30 border border-border text-center">
                  <CheckCircle className="h-5 w-5 text-emerald-400 mx-auto mb-2" />
                  <p className="text-sm font-semibold text-foreground">{item.title}</p>
                  <p className="text-xs text-muted-foreground mt-1">{item.desc}</p>
                </div>
              ))}
            </div>

            <h3 className="text-lg font-semibold text-foreground mt-8 mb-3">Simple Example</h3>
            <div className="rounded-xl border border-border p-5 bg-emerald-500/5 border-emerald-500/20">
              <p className="text-sm text-muted-foreground leading-relaxed">
                <strong className="text-foreground">Scenario:</strong> A customer buys a digital course for 30 USDT on your website.
              </p>
              <div className="mt-3 space-y-2 text-sm text-muted-foreground">
                <p>1. Your website creates a 30 USDT invoice via the CoinPayUp API.</p>
                <p>2. The customer sends 30 USDT to the invoice wallet.</p>
                <p>3. CoinPayUp confirms the payment and sends an <code className="px-1 py-0.5 rounded bg-muted text-foreground text-xs font-mono">invoice.paid</code> event to your webhook.</p>
                <p>4. Your system receives the event and <strong className="text-foreground">automatically grants the customer access to the course</strong> — no manual action needed.</p>
              </div>
            </div>
          </Section>

          {/* CHOOSING THE RIGHT INTEGRATION */}
          <Section id="choosing-integration" title="Choosing the Right Integration">
            <p>
              Not sure which integration method is right for you? Use this quick guide to find the best fit based on your situation.
            </p>

            <div className="overflow-x-auto my-6">
              <table className="w-full text-sm border border-border rounded-lg overflow-hidden">
                <thead>
                  <tr className="bg-muted/50">
                    <th className="text-left px-4 py-3 font-semibold text-foreground border-b border-border">Your Situation</th>
                    <th className="text-left px-4 py-3 font-semibold text-foreground border-b border-border">Recommended Integration</th>
                    <th className="text-left px-4 py-3 font-semibold text-foreground border-b border-border">Why</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['I don\'t have a website', 'Payment Links', 'No setup needed — just create and share a link.'],
                    ['I use WordPress / WooCommerce', 'Plugin', 'Install the plugin and start accepting crypto in your store.'],
                    ['I use WHMCS', 'Plugin', 'Add crypto payments to your hosting platform.'],
                    ['I have a developer on my team', 'API', 'Build a custom checkout flow tailored to your business.'],
                    ['I run a SaaS platform', 'API + Webhooks', 'Automate billing and subscription management.'],
                    ['I\'m a freelancer or consultant', 'Payment Links', 'Send invoices via WhatsApp, email, or social media.'],
                    ['I sell on social media', 'Payment Links', 'Share your payment link in posts, stories, or DMs.'],
                    ['I need automated order fulfillment', 'API + Webhooks', 'Your system processes payments without manual intervention.'],
                  ].map(([situation, rec, why], i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-muted/20' : ''}>
                      <td className="px-4 py-2.5 border-b border-border text-foreground">{situation}</td>
                      <td className="px-4 py-2.5 border-b border-border font-medium text-primary">{rec}</td>
                      <td className="px-4 py-2.5 border-b border-border text-muted-foreground">{why}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <InfoBox type="success" title="Start simple, upgrade later">
              You don't have to pick the most advanced option on day one. Many merchants start with payment links, then upgrade to plugins or API integration as their business grows. CoinPayUp makes it easy to level up without losing any data or settings.
            </InfoBox>
          </Section>

          {/* BEST PRACTICES */}
          <Section id="best-practices" title="Integration Best Practices">
            <p>Follow these recommendations to ensure a smooth and reliable integration with CoinPayUp.</p>

            <div className="space-y-4 mt-6">
              {[
                { title: 'Test payments before going live', desc: 'Use CoinPayUp\'s test mode to simulate payments before accepting real money. Create test invoices, verify your webhook endpoint receives events, and confirm your success/cancel URLs work correctly. This catches issues before your customers see them.' },
                { title: 'Make sure your webhook URL is always active', desc: 'Your webhook endpoint should be reliable and always online. If it goes down, CoinPayUp will retry — but prolonged downtime means delayed payment processing. Consider using a service like AWS, Google Cloud, or Vercel for hosting your webhook handler.' },
                { title: 'Verify your success & cancel URLs', desc: 'After configuring your callback URLs, test them manually by navigating to them in your browser. Make sure the success page shows order confirmation and the cancel page provides clear next steps for the customer.' },
                { title: 'Keep your API keys secure', desc: 'Never expose your API keys in frontend code, public repositories, or client-side JavaScript. Store them in environment variables on your server. If you suspect a key has been compromised, rotate it immediately from the dashboard.' },
                { title: 'Handle all payment scenarios', desc: 'Don\'t just handle successful payments. Make sure your integration also handles partial payments (send the customer a follow-up), expired invoices (cancel the order), and overpayments (flag for review). This creates a reliable experience for your customers.' },
                { title: 'Verify webhook signatures', desc: 'Every webhook from CoinPayUp includes a cryptographic signature. Always verify this signature before processing the event. This prevents attackers from sending fake payment notifications to your server.' },
                { title: 'Respond to webhooks quickly', desc: 'Your webhook endpoint should return HTTP 200 within a few seconds. Do heavy processing (sending emails, updating databases) asynchronously after responding. Slow responses cause unnecessary retries.' },
                { title: 'Log everything', desc: 'Keep a record of every webhook event, API response, and payment status change. This helps with debugging, customer support, and financial reconciliation.' },
              ].map((item, i) => (
                <div key={i} className="flex gap-3 p-4 rounded-xl bg-muted/30 border border-border">
                  <CheckCircle className="h-5 w-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-foreground">{item.title}</p>
                    <p className="text-sm text-muted-foreground mt-0.5">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <InfoBox type="warning" title="Most common mistake">
              The single most common integration mistake is <strong>relying only on browser redirects</strong> to confirm payments. Redirects are a user experience feature — not a payment confirmation method. A customer can close their browser, lose internet, or manually navigate to your success URL. Always use webhooks or server-side API checks to verify that a payment was actually received before fulfilling an order.
            </InfoBox>
          </Section>

        </main>
      </div>

      <SiteFooter />
    </div>
  );
};

export default DocsIntegrationsPage;
